import Actions from 'flow/actions';
import { reportCompanionLocationStatus } from 'flow/companion/companion-actions';
import { CompanionLocationStatus } from 'flow/companion/companion-mode';
import {
    selectGeoLocationCoordinates,
    selectGeoLocationIsFresh,
    selectReportableLocationStatus,
} from 'flow/selectors/geolocation-selectors';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import {
    GEO_FIX_WAIT_MS,
    GeoLocationCoordinates,
    checkGeoRestrictionIsEnabled,
    requestGeoLocation,
} from 'flow/utils/geolocation-utils';

type OnFix = (coordinates: GeoLocationCoordinates) => void;

export const setGeoLocation = (
    coordinates: GeoLocationCoordinates | null,
    permissionDenied = false,
) => ({
    type: Actions.GEO_LOCATION,
    coordinates,
    permissionDenied,
    timestamp: Date.now(),
});

export const setGeoOutsideFence = (outsideFence: boolean) => ({
    type: Actions.GEO_OUTSIDE_FENCE,
    outsideFence,
});

export const setGeoRestrictionEnabled = (enabled: boolean | null) => ({
    type: Actions.GEO_RESTRICTION_ENABLED,
    enabled,
});

export const fetchGeoRestrictionEnabled =
    () =>
    async (dispatch: AppDispatch, getState: () => State): Promise<void> => {
        if (getState().app.get('geoRestrictionEnabled') !== undefined) {
            return;
        }

        dispatch(
            setGeoRestrictionEnabled(await checkGeoRestrictionIsEnabled()),
        );
    };

export const reportLocationStatusToSurfaces =
    () => (dispatch: AppDispatch, getState: () => State) => {
        const status = selectReportableLocationStatus(getState());

        if (status) {
            dispatch(reportCompanionLocationStatus(status));
        }
    };

export const reportOutsideRegionToSurfaces = () => (dispatch: AppDispatch) =>
    dispatch(
        reportCompanionLocationStatus(CompanionLocationStatus.OUTSIDE_REGION),
    );

export const resolveGeoLocation =
    (timeoutMs?: number, onLateFix?: OnFix) =>
    async (
        dispatch: AppDispatch,
        getState: () => State,
    ): Promise<GeoLocationCoordinates | null> => {
        const { coordinates, permissionDenied } = await requestGeoLocation(
            timeoutMs,
            (lateCoordinates) => {
                dispatch(setGeoLocation(lateCoordinates));
                dispatch(reportLocationStatusToSurfaces());
                onLateFix?.(lateCoordinates);
            },
        );
        const resolved =
            coordinates || permissionDenied
                ? coordinates
                : selectGeoLocationCoordinates(getState());

        dispatch(setGeoLocation(resolved, permissionDenied));
        dispatch(reportLocationStatusToSurfaces());

        return resolved;
    };

export const ensureFreshGeoLocation =
    (onLateFix?: OnFix) =>
    async (
        dispatch: AppDispatch,
        getState: () => State,
    ): Promise<GeoLocationCoordinates | null> => {
        if (selectGeoLocationIsFresh(getState())) {
            return selectGeoLocationCoordinates(getState());
        }

        return dispatch(resolveGeoLocation(GEO_FIX_WAIT_MS, onLateFix));
    };
